"use client"
import { Champignons } from "@repo/types";
import { useLogger } from "@repo/ui/lib/logger/index";
import { ColumnDef } from "@tanstack/react-table";
import { Eye, Pencil, RefreshCw, Trash2 } from "lucide-react";
import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/alert-dialog";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { DataTable } from "../../components/ui/data-table";
import DataTableSearch from "../../components/ui/data-table/data-table-search";
import { StatusBadge } from "../../components/ui/data-table/status-badge";
import { Label } from "../../components/ui/label";
import { ListSortSelect } from "../../components/ui/list-sort-select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import { useDeleteChampignon, useRestoreChampignon } from "../../hooks/api/champignons";
import { formatDate } from "../../lib/utils";
import { ChampignonTableImageCell } from "./components/champignon-table-image-cell";
import {
  getChampignonCategoryBadgeStyle,
  getChampignonCategoryLabel,
} from "./utils/champignon-category";

interface ChampignonListProps {
  champignons: Champignons[];
  isLoading: boolean;
  error: Error | null;
  onView: (id: number) => void;
  onEdit: (id: number) => void;
  page: number;
  limit: number;
  total: number;
  sortBy: string;
  onLimitChange: (limit: number) => void;
  onSearchChange: (search: string) => void;
  searchTerm: string;
  onPageChange: (page: number) => void;
  handleSortChange: (sort: string) => void;
  showDeleted: boolean;
  onShowDeletedChange: (show: boolean) => void;
  pageCount: number;
  mode?: "manage" | "browse";
}

const CHAMPIGNON_SORT_OPTIONS = [
  { value: "name", label: "Nom" },
  { value: "recent", label: "Plus récents" },
  { value: "oldest", label: "Plus anciens" },
] as const;

export function ChampignonsList({
  champignons,
  isLoading,
  error,
  onView,
  onEdit,
  page,
  limit,
  total,
  sortBy,
  onLimitChange,
  onSearchChange,
  searchTerm,
  onPageChange,
  handleSortChange,
  showDeleted,
  pageCount,
  onShowDeletedChange,
  mode = "manage",
}: ChampignonListProps) {
  const logger = useLogger("ChampignonList");
  const [champignonToDelete, setChampignonToDelete] = useState<number | null>(null);

  logger.debug("ChampignonList rendered", { page, limit, total, sortBy });

  const isBrowseMode = mode === "browse";

  const deleteChampignonMutation = useDeleteChampignon();
  const restoreChampignonMutation = useRestoreChampignon();

  const handleConfirmDelete = () => {
    if (champignonToDelete) {
      deleteChampignonMutation.mutate(champignonToDelete, {
        onSuccess: () => setChampignonToDelete(null),
        onError: (err) => console.error("Error deleting champignon:", err),
      });
    }
  };

  const handleRestoreClick = (id: number) => {
    restoreChampignonMutation.mutate(id, {
      onError: (err) => console.error("Error restoring champignon:", err),
    });
  };

  // ─── Columns ────────────────────────────────────────────────────────────────

  const columns: ColumnDef<Champignons>[] = [
    {
      id: "img_url",
      header: "Image",
      cell: ({ row }) => <ChampignonTableImageCell champignon={row.original} />,
    },
    {
      accessorKey: "name",
      header: "Nom",
      cell: ({ row }) => (
        <span className="font-medium text-sm">{row.original.name || "—"}</span>
      ),
    },
    {
      accessorKey: "category",
      header: "Catégorie",
      cell: ({ row }) => {
        const category = row.original.category;
        const label = getChampignonCategoryLabel(category);
        const badgeStyle = getChampignonCategoryBadgeStyle(category);

        if (!label || !badgeStyle) {
          return <span className="text-sm text-muted-foreground">—</span>;
        }

        return <Badge className={badgeStyle.className}>{label}</Badge>;
      },
    },
    {
      accessorKey: "attaque_f",
      header: "Attaque Feuillu",
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {row.original.attaque_f || "—"}
        </span>
      ),
    },
    {
      accessorKey: "attaque_r",
      header: "Attaque Résineux",
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {row.original.attaque_r || "—"}
        </span>
      ),
    },
    {
      accessorKey: "created_at",
      header: "Créé le",
      cell: ({ row }) => {
        const date = row.original.created_at;
        return (
          <span className="text-sm text-muted-foreground">
            {date ? formatDate(new Date(date)) : "—"}
          </span>
        );
      },
    },
    {
      accessorKey: "status",
      header: "Statut",
      cell: ({ row }) => (
        <StatusBadge
          tone={row.original.is_deleted ? "deleted" : "active"}
          label={row.original.is_deleted ? "Supprimé" : "Actif"}
          withIcon
        />
      ),
    },
    ...(!isBrowseMode
      ? [
          {
      id: "actions",
      header: () => <div className="text-right pr-2">Actions</div>,
      cell: ({ row }) => {
        const champignon = row.original;
        return (
          <div className="flex justify-end items-center gap-1 pr-2">
            {/* View */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={() => onView(champignon.id)}
                  size="sm"
                  variant="ghost"
                  className="h-8 w-8 p-0"
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Voir le détail</TooltipContent>
            </Tooltip>

            {/* Edit */}
            {!champignon.is_deleted && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={() => onEdit(champignon.id)}
                    size="sm"
                    variant="ghost"
                    className="h-8 w-8 p-0"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Modifier</TooltipContent>
              </Tooltip>
            )}

            {/* Delete / Restore */}
            {!champignon.is_deleted ? (
              <AlertDialog
                open={champignonToDelete === champignon.id}
                onOpenChange={(open) =>
                  setChampignonToDelete(open ? champignon.id : null)
                }
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <AlertDialogTrigger asChild>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                  </TooltipTrigger>
                  <TooltipContent>Supprimer</TooltipContent>
                </Tooltip>

                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Supprimer ce champignon ?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Cette action est réversible. Le champignon sera marqué comme
                      supprimé et pourra être restauré ultérieurement.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleConfirmDelete}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Supprimer
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 w-8 p-0"
                    onClick={() => handleRestoreClick(champignon.id)}
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Restaurer</TooltipContent>
              </Tooltip>
            )}
          </div>
        );
      },
          } satisfies ColumnDef<Champignons>,
        ]
      : []),
  ];

  // ─── Error state ─────────────────────────────────────────────────────────────

  if (error)
    return (
      <Alert variant="destructive">
        <AlertDescription>Erreur : {error.message}</AlertDescription>
      </Alert>
    );

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Search — grows to fill available space */}
        <div className="flex-1 min-w-[200px]">
          <DataTableSearch
            value={searchTerm}
            onChange={onSearchChange}
            placeholder="Rechercher par nom…"
          />
        </div>

        <ListSortSelect
          id="champignon-sort"
          value={sortBy}
          options={[...CHAMPIGNON_SORT_OPTIONS]}
          onValueChange={handleSortChange}
        />

        {/* Show deleted toggle */}
        {!isBrowseMode && (
          <div className="flex items-center gap-2">
            <Checkbox
              id="showDeleted"
              checked={showDeleted}
              onCheckedChange={(checked) => onShowDeletedChange(checked === true)}
            />
            <Label
              htmlFor="showDeleted"
              className="text-sm text-muted-foreground cursor-pointer select-none"
            >
              Afficher supprimés
            </Label>
          </div>
        )}
      </div>

      {/* Table */}
      <DataTable
        serverSidePagination
        columns={columns}
        data={champignons}
        isLoading={isLoading}
        renderSearchOutside
        externalGlobalFilter={searchTerm}
        onGlobalFilterChange={onSearchChange}
        pageCount={pageCount}
        pageSize={limit}
        totalCount={total}
        onPageChange={onPageChange}
        onPageSizeChange={onLimitChange}
        searchPlaceholder="Rechercher un champignon…"
        currentPage={page}
      />
    </div>
  );
}
